import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Switch,
  TextField,
  Button,
  FormControlLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { clinicPaymentsService, type PaymentSettings, type WebhookEventRow } from '@services/clinicPaymentsService';

export default function PaymentSettingsPage() {
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [events, setEvents] = useState<WebhookEventRow[]>([]);

  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<'test' | 'live'>('test');

  useEffect(() => {
    clinicPaymentsService
      .getPaymentSettings()
      .then((d) => {
        setSettings(d);
        setPublishableKey(d.publishableKey || '');
        setEnabled(d.enabled);
        setMode(d.mode === 'live' ? 'live' : 'test');
      })
      .catch((e) => setError(e?.response?.data?.error || 'Eroare la încărcare'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!settings) return;
    clinicPaymentsService.getWebhookEvents(20).then(setEvents).catch(() => setEvents([]));
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await clinicPaymentsService.updatePaymentSettings({
        enabled,
        mode,
        publishableKey: publishableKey.trim() || undefined,
        secretKey: secretKey.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined
      });
      setMessage('Setări salvate.');
      setSecretKey('');
      setWebhookSecret('');
      clinicPaymentsService.getPaymentSettings().then(setSettings);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      await clinicPaymentsService.testPaymentConfig();
      setMessage('Configurare validă.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Configurare invalidă');
    } finally {
      setTesting(false);
    }
  };

  const readOnly = settings?.managedByMaster === true;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Setări plăți (Stripe)
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Setările de plăți sunt configurate de administrator. Dreptul de folosire v-a fost acordat clinicii dumneavoastră. Pentru modificări (chei Stripe, webhook, activare/dezactivare) contactați administratorul.
        </Alert>
      )}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={readOnly} />}
          label="Plăți online activate"
        />
        <FormControlLabel
          control={
            <Switch
              checked={mode === 'live'}
              onChange={(e) => setMode(e.target.checked ? 'live' : 'test')}
              disabled={readOnly}
            />
          }
          label="Mod live (test dezactivat)"
        />
        {!readOnly && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
            <TextField
              label="Publishable key"
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_test_... sau pk_live_..."
              fullWidth
              size="small"
            />
            <TextField
              label="Secret key"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={settings?.hasSecret ? '••••••••' : 'sk_test_...'}
              fullWidth
              size="small"
              helperText="Lăsați gol pentru a păstra valoarea existentă"
            />
            <TextField
              label="Webhook secret"
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={settings?.hasWebhookSecret ? '••••••••' : 'whsec_...'}
              fullWidth
              size="small"
              helperText="Lăsați gol pentru a păstra valoarea existentă"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={24} /> : 'Salvează'}
              </Button>
              <Button variant="outlined" onClick={handleTest} disabled={testing}>
                {testing ? <CircularProgress size={24} /> : 'Testează configurarea'}
              </Button>
            </Box>
          </Box>
        )}
        {readOnly && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Status: {enabled ? 'Plăți online activate' : 'Plăți online dezactivate'} • Mod: {mode === 'live' ? 'Live' : 'Test'}
            </Typography>
          </Box>
        )}
      </Paper>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Ultimele evenimente webhook
      </Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tip</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Eroare</TableCell>
              <TableCell>Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>Niciun eveniment</TableCell>
              </TableRow>
            ) : (
              events.map((ev) => (
                <TableRow key={ev.id}>
                  <TableCell>{ev.event_type}</TableCell>
                  <TableCell>{ev.status}</TableCell>
                  <TableCell>{ev.error_message || '-'}</TableCell>
                  <TableCell>{ev.processed_at ? new Date(ev.processed_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
